import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { TriangleAlert } from 'lucide-react';
import { Button, Card, EmptyState, Toast, buttonVariants } from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { leaveTo } from '@wundo/client/leaveTo.ts';
import { requireSession } from '@wundo/client/session.ts';
import { loginUrl } from '@/lib/config.ts';
import { answerOAuthConsent, fetchOAuthClientName } from '@/lib/mcp.ts';
import { oauthDestination } from '@/lib/oauthDestination.ts';

/*
 * Where the backend's OAuth server sends a superadmin connecting Claude or ChatGPT:
 * first to sign in, then to allow the assistant in. Outside the admin shell,
 * because it is a step in someone else's flow rather than a page of ours.
 *
 * The raw query is what the backend signed, so it is read from the address
 * bar and handed back byte for byte rather than through the router's parser.
 */
export const Route = createFileRoute('/connect')({
  beforeLoad: async ({ context }) => {
    await requireSession(
      context.queryClient,
      `${window.location.pathname}${window.location.search}`,
      { loginUrl: loginUrl() },
    );
  },
  component: ConnectPage,
});

function ConnectPage() {
  const oauthQuery = window.location.search.slice(1);
  const params = new URLSearchParams(oauthQuery);
  const clientId = params.get('client_id') ?? '';
  /* Already checked against the client's registered addresses before it got here. */
  const destination = oauthDestination(params.get('redirect_uri'));

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const { data: clientName } = useQuery({
    queryKey: ['oauthClientName', clientId],
    queryFn: () => fetchOAuthClientName(clientId),
    enabled: clientId !== '' && user?.role === 'superadmin',
  });

  const answer = useMutation({
    mutationFn: (accept: boolean) => answerOAuthConsent(accept, oauthQuery),
    onSuccess: (url) => leaveTo(url),
  });

  if (!user) return null;

  if (user.role !== 'superadmin') {
    return (
      <main className='flex h-dvh items-center justify-center px-7'>
        <EmptyState
          title='Only superadmins can connect an assistant'
          description='This connection gives an AI assistant read access to how Wundo is built, to your own meetings and to what Wundo remembers about the people you work with, and lets it query Wundo’s database read-only. It is available to superadmins only.'
          action={
            <Link to='/' className={buttonVariants({ variant: 'accent' })}>
              Go to the start page
            </Link>
          }
        />
      </main>
    );
  }

  const assistant = clientName ?? 'This assistant';

  return (
    <main className='flex h-dvh items-center justify-center px-7'>
      <Card className='flex max-w-120 flex-col gap-4'>
        <h1 className='text-xl font-semibold tracking-tight text-ink'>
          Connect {assistant} to Wundo
        </h1>
        <p className='text-sm text-ink-body'>
          {assistant} will be able to read Wundo&apos;s design docs, prompts and skills,
          your own meetings in Wundo with their preparation, verslag, notes and
          transcript, and what Wundo remembers about the people you work with, and to
          query Wundo&apos;s database read-only. It acts as you and cannot change
          anything.
        </p>

        <div className='flex flex-col gap-1 rounded-md bg-fill p-3.5'>
          <p className='text-xs text-ink-muted'>Access will be sent to</p>
          <p className='font-mono text-base font-semibold break-all text-ink'>
            {destination?.host ?? 'Unknown'}
          </p>
        </div>

        {destination?.local && (
          <div role='alert' className='flex gap-3 rounded-md bg-pink-tint p-3.5'>
            <TriangleAlert
              className='mt-0.5 size-5 shrink-0 text-on-pink'
              aria-hidden='true'
            />
            <p className='text-sm text-on-pink'>
              This is a program on this computer, not a website. Only allow it if you
              started this connection yourself, for example from Claude Code.
            </p>
          </div>
        )}

        <p className='text-xs text-ink-muted'>
          The assistant chose its own name; Wundo does not check it. Only allow this if
          you recognise the address above.
        </p>

        <div className='flex gap-2'>
          <Button
            variant='accent'
            loading={answer.isPending && answer.variables}
            disabled={answer.isPending || !destination}
            onClick={() => answer.mutate(true)}
          >
            Allow
          </Button>
          <Button
            variant='ghost'
            disabled={answer.isPending}
            onClick={() => answer.mutate(false)}
          >
            Cancel
          </Button>
        </div>
      </Card>

      <Toast
        open={answer.isError}
        message='Could not finish connecting. Start again from your assistant.'
      />
    </main>
  );
}
